import React, { useState } from "react";
import LpSection from "./LpSection/LpSection";
import ProfileImage from "./ProfileImage/ProfileImage";
import "./LpGenerator.css";
import ProfileName from "./ProfileName/ProfileName";
import ProfileLinks from "./ProfileLinks/ProfileLinks";
import HeadLine from "./Headline/HeadLine";
import SubHeader from "./SubHeader/SubHeader";
import Image from "./Image/Image";
import RichText from "./RichText/RichText";
import ListHighlighted from "./ListHighlighted/ListHighlighted";
import Whatsapp from "./Whatsapp/Whatsapp";
import ListOrdinary from "./ListOrdinary/ListOrdinary";
import Slider from "./Slider/Slider";
import Swiper from "./Swiper/Swiper";
import Space from "./Space/Space";
import Question from "./Question/Question";
import Review from "./Reviews/Review";
import Certificate from "./Certificate/Certificate";
import Form from "./Form/Form";
import ConsultationForm from "./ConsultationForm/ConsultationForm";
import ThankYou from "./ThankYou/ThankYou";
import WebinarCompleted from "./WebinarCompleted/WebinarCompleted";
import IconTextRow from "./IconTextRow/IconTextRow";
import CapturableSlide from "./CapturableSlide/CapturableSlide";
import { hasCompletedWebinarPreload } from "../../utls/preloadRequests";

const LpGenerator = ({ data, preloads }) => {
    const [isRegistered, setIsRegistered] = useState(false);
    const [registrationResult, setRegistrationResult] = useState(null);

    if (hasCompletedWebinarPreload(preloads)) {
        return <WebinarCompleted />;
    }

    if (isRegistered) {
        return (
            <ThankYou
                whatsappGroupUrl={registrationResult?.whatsappGroupUrl}
            />
        );
    }

    const renderComponent = (component, index) => {
        if (!component) {
            return null;
        }

        if (component.name === "profile-image") {
            return <ProfileImage key={index} imgUrl={component.url} />;
        } else if (component.name === "profile-name") {
            return <ProfileName key={index} name={component.text} />;
        } else if (component.name === "profile-links") {
            return <ProfileLinks key={index} links={component.links} />;
        } else if (component.name === "headline") {
            return <HeadLine key={index} text={component.text} />;
        } else if (component.name === "subheader") {
            return <SubHeader key={index} text={component.text} />;
        } else if (component.name === "image") {
            return <Image key={index} imgUrl={component.imgUrl} />;
        } else if (component.name === "richtext") {
            return <RichText key={index} text={component.text} />;
        } else if (component.name === "iconTextRow") {
            return (
                <IconTextRow
                    key={component.id || index}
                    icon={component.icon}
                    text={component.text}
                    alt={component.alt}
                />
            );
        } else if (component.name === "listHighlighted") {
            return <ListHighlighted key={index} list={component.elements} />;
        } else if(component.name === "reviews") {
            return <Slider  momentum={true} sensitivity={1.2} key={index} isText={true}>
                {component.reviews.map((review, reviewIndex) => (
                    <Review
                        key={review.id || reviewIndex}
                        review={review}
                    />
                ))}
            </Slider>
        } else if( component.name === "certificates") {
            return <Slider  momentum={true} sensitivity={1.2} key={index}>
                {component.certificates.map((certificate, certificateIndex) => (
                    <Certificate
                        key={certificate.id || certificateIndex}
                        certificate={certificate}
                    />
                ))}
            </Slider>
        } else if (component.name === "whatsapp") {
            return (
                <Whatsapp
                    key={index}
                    text={component.text}
                    message={component.message}
                    phone={component.phone}
                    icon={component.icon}
                />
            );
        } else if (component.name === "space") {
            return <Space key={index} space={component.space} />;
        } else if (component.name === "list") {
            return <ListOrdinary key={index} items={component.elements} />;
        } else if (component.name === "slide") {
            const slideBackground =
                component.background ||
                component.backgroundColor ||
                "transparent";

            return (
                <CapturableSlide
                    key={component.id || index}
                    background={slideBackground}
                    fileName={
                        component.storyFileName ||
                        component.id ||
                        `service-card-${index + 1}`
                    }
                    captureEnabled={component.capture !== false}
                >
                    {component?.components?.map((child, childIndex) =>
                        renderComponent(child, childIndex)
                    )}
                </CapturableSlide>
            );
        } else if (component.name === "questions") {
            return (
                <Slider  momentum={true} sensitivity={1.2} key={index} isText={true}>
                    {component.questions.map((q, qIndex) => (
                        <Question key={q.id || qIndex} q={q} />
                    ))}
                </Slider>
            );
        } else if (component.name === "slider") {
            return (
                <Slider
                    momentum={true}
                    sensitivity={1.2}
                    key={index}
                    isText={component.isText}
                    itemWidth={component.itemWidth}
                    gap={component.gap}
                    padding={component.padding}
                    ariaLabel={component.ariaLabel}
                >
                    {component?.components?.map((comp, componentIndex) =>
                        renderComponent(comp, componentIndex)
                    )}
                </Slider>
            );
        } else if (component.name === "swiper") {
            return (
                <Swiper
                    key={index}
                    gap={component.gap ?? 12}
                    drag={component.drag ?? true}
                    showArrows={component.showArrows ?? false}
                    showDots={component.showDots ?? true}
                    ariaLabel={component.ariaLabel || "Content carousel"}
                >
                    {component?.components?.map((comp, componentIndex) =>
                        renderComponent(comp, componentIndex)
                    )}
                </Swiper>
            );
        }

        return null;
    };

    const renderSection = (section, index) => {
        if (section?.name === "form") {
            return (
                <LpSection key={index}>
                    <Form
                        api={section.api}
                        components={section.components}
                        onSuccess={(result) => {
                            setRegistrationResult(result?.response || null);
                            setIsRegistered(true);
                        }}
                    />
                </LpSection>
            );
        }

        if (section?.name === "consultationForm") {
            return (
                <LpSection key={index}>
                    <ConsultationForm
                        phone={section.phone}
                        components={section.components}
                    />
                </LpSection>
            );
        }

        return (
            <LpSection key={index}>
                {section?.components?.map((component, componentIndex) =>
                    renderComponent(component, componentIndex)
                )}
            </LpSection>
        );
    };

    return (
        <div className={`LpGenerator${data?.name === "consultation" ? " LpGenerator--consultation" : ""}`}>
            {data?.sections
                ?.filter((section) => section?.name !== "preload")
                .map((section, index) => renderSection(section, index))}
        </div>
    );
};

export default LpGenerator;
